/**
 * CafeInvitationManager
 *
 * Manages cafe-type invitation lifecycle (Pavilion mode).
 *
 * Responsibilities:
 * - Monitor cafe invitation state changes
 * - Handle invitation invalidation cleanup
 * - Check soft kick expiration
 * - Synchronize invitation players with connected players
 * - Update Playjector with Steam guest login mode
 * - Handle lobby owner changes
 *
 * Source: apps/host/host-frontend/src/api/modules/events/invitationEvents.ts (cafe-specific)
 */

import type { StateStore } from '@playcastdotio/CoreTypes';
import type {
  InvitationState,
  HostMatchmakingState,
  MatchmakingState as MatchmakerState,
} from '../PavilionCoordinator';
import type { CoordinatorEvent } from '../../_types/events';

type PlayerState = {
  players: Record<string, any>;
};

type HostState = {
  status: string;
  edition: string;
};

type CafeInvitationStores = {
  invitation: StateStore<InvitationState>;
  hostMatchmaking: StateStore<HostMatchmakingState>;
  matchmaker: StateStore<MatchmakerState>;
  player: StateStore<PlayerState>;
  host: StateStore<HostState>;
};

interface CafeInvitationEvents {
  invitationInvalidated: CoordinatorEvent<{ invitationId: string; reason?: string }>;
  softKickExpired: CoordinatorEvent<{ invitationId: string }>;
  lobbyOwnerChanged: CoordinatorEvent<{ oldOwnerId: string; newOwnerId: string }>;
  steamGuestLoginChanged: CoordinatorEvent<{ enabled: boolean }>;
}

export class CafeInvitationManager {
  private cachedInvitationState: InvitationState | null = null;

  constructor(
    private stores: CafeInvitationStores,
    private events: CafeInvitationEvents,
  ) {}

  /**
   * Initialize the manager
   * Sets up listeners for invitation state changes
   */
  init(): void {
    console.log('[CafeInvitationManager] Initialized');

    // TODO: Set up invitation state listener
    // this.stores.invitation.addListener((invState) => {
    //   this.handleInvitationChange(invState);
    // });
  }

  /**
   * Handle invitation state changes (cafe-type only)
   *
   * This is the main entry point for cafe invitation lifecycle logic.
   * Called when the invitation state store updates.
   */
  private handleInvitationChange(invState: InvitationState): void {
    const isPavilion = this.stores.host.currentState.edition === 'pavilion';
    if (!isPavilion || invState.type !== 'cafe') {
      return;
    }

    console.log('[CafeInvitationManager] Cafe invitation state changed:', invState);

    // Check soft kick expiration
    if (invState.manifest?.softKickExpirationTime) {
      this.checkSoftKickExpiration(invState);
    }

    // Handle invitation invalidation
    if (invState.invalid && this.cachedInvitationState && !this.cachedInvitationState.invalid) {
      this.handleInvitationInvalidation(invState);
    }

    // Handle soft kick cleanup
    if (invState.invalid && this.cachedInvitationState && !this.cachedInvitationState.invalid) {
      if (invState.manifest?.softKickExpirationTime) {
        this.handleSoftKickCleanup(invState);
      }
    }

    // Check player ID synchronization
    if (this.cachedInvitationState?.type === 'cafe') {
      this.checkPlayerIds(this.cachedInvitationState, invState);
    }

    // Handle Steam guest login changes
    this.handleSteamGuestLoginChange(invState);

    // Handle lobby owner changes
    if (this.cachedInvitationState) {
      this.checkLobbyOwnerChange(this.cachedInvitationState, invState);
    }

    this.cachedInvitationState = invState;
  }

  /**
   * Check if soft kick has expired
   */
  private checkSoftKickExpiration(invState: InvitationState): void {
    if (invState.type !== 'cafe' || !invState.manifest) return;
    const expirationTime = invState.manifest.softKickExpirationTime;
    if (!expirationTime) return;

    console.log('[CafeInvitationManager] Soft kick expiration time:', expirationTime);

    // TODO: Implement soft kick expiration check
    // This would call checkSoftKickExpiration from managers/softKick
  }

  /**
   * Handle invitation invalidation
   * Kicks players and stops stream when invitation becomes invalid
   */
  private handleInvitationInvalidation(invState: InvitationState): void {
    console.log('[CafeInvitationManager] Invitation invalidated, cleaning up session');

    try {
      // CRITICAL: Kick players FIRST before stopping stream
      // This ensures playerCount drops to 0, triggering Ad Mode restart
      const playerIds = Object.keys(this.stores.player.currentState.players);
      if (playerIds.length > 0) {
        console.log('[CafeInvitationManager] Kicking remaining players due to invalidation:', playerIds);
        // TODO: Call IPC function to kick all players
        // ipcFunctions?.kickAll();
      }

      // Stop the stream if still streaming
      if (this.stores.host.currentState.status === 'streaming') {
        console.log('[CafeInvitationManager] Stopping stream due to invalidation');
        // TODO: Call IPC function to stop stream
        // ipcFunctions?.stopStream('invitation:invalidated');
      }

      // Reset matchmaker state
      this.stores.matchmaker.reset();

      // Emit event
      this.events.invitationInvalidated.emit({
        invitationId: invState.id,
        reason: 'invalidated',
      });
    } catch (error) {
      console.error('[CafeInvitationManager] Failed to clean up session on invalidation:', error);
    }
  }

  /**
   * Handle soft kick cleanup
   */
  private handleSoftKickCleanup(invState: InvitationState): void {
    console.log(`[CafeInvitationManager] Received invalidation confirmation for invitation ${invState.id}, performing cleanup`);

    // Clear invitation state and localStorage
    this.stores.invitation.reset();
    localStorage.removeItem('currentInvitation');

    // Remove host from matchmaking
    this.stores.hostMatchmaking.updateState({
      ...this.stores.hostMatchmaking.currentState,
      matchmakeable: false,
    });

    // TODO: Send the updated config to the API
    // sendMatchmakerHostConfig().catch((err) => {
    //   console.error('[CafeInvitationManager] Failed to send host config after cleanup:', err);
    // });

    this.events.softKickExpired.emit({ invitationId: invState.id });

    console.log(`[CafeInvitationManager] Successfully completed cleanup for soft kick of invitation ${invState.id}`);
  }

  /**
   * Check and synchronize player IDs between invitation and connected players
   */
  private checkPlayerIds(oldInvState: InvitationState, newInvState: InvitationState): void {
    if (oldInvState.type !== 'cafe' || newInvState.type !== 'cafe') return;
    const oldPlayerIds = oldInvState.manifest?.players?.map((player) => player.playerId || '') || [];
    const newPlayerIds = newInvState.manifest?.players?.map((player) => player.playerId || '') || [];
    const removedPlayerIds = oldPlayerIds.filter((id) => !newPlayerIds.includes(id));

    if (removedPlayerIds.length > 0 || newPlayerIds.length !== oldPlayerIds.length) {
      // TODO: Call checkPlayerIds from peers/host
      // checkPlayerIds(newPlayerIds, removedPlayerIds);
      console.log('[CafeInvitationManager] Player IDs changed:', { newPlayerIds, removedPlayerIds });
    }
  }

  /**
   * Handle Steam guest login mode changes
   */
  private handleSteamGuestLoginChange(invState: InvitationState): void {
    if (invState.type !== 'cafe' || !invState.manifest) return;
    const currentSteamGuestLogin = invState.manifest.useSteamGuestLogin ?? false;
    const cachedSteamGuestLogin =
      this.cachedInvitationState?.type === 'cafe' && this.cachedInvitationState.manifest
        ? (this.cachedInvitationState.manifest.useSteamGuestLogin ?? false)
        : false;

    // Only send update if:
    // 1. This is the first invitation (no cached state)
    // 2. The steam guest login setting has changed
    // 3. The invitation ID has changed (new invitation)
    const shouldSendUpdate =
      !this.cachedInvitationState ||
      currentSteamGuestLogin !== cachedSteamGuestLogin ||
      this.cachedInvitationState.id !== invState.id;

    if (shouldSendUpdate) {
      console.log('[CafeInvitationManager] Updating playjector with steam guest login:', currentSteamGuestLogin);

      // TODO: Send IPC message to update Playjector
      // ipcSend(
      //   generators.host.hostModeChanged(
      //     'pavilion',
      //     hostMatchmakingState.currentState.matchmakeable,
      //     currentSteamGuestLogin,
      //     hostMatchmakingState.currentState.guestAccount,
      //     steamCredentialsState.currentState,
      //     authState.currentState.hostAuthId,
      //   ),
      // );

      this.events.steamGuestLoginChanged.emit({ enabled: currentSteamGuestLogin });
    }
  }

  /**
   * Check for lobby owner changes and update player permissions
   */
  private checkLobbyOwnerChange(oldInvState: InvitationState, newInvState: InvitationState): void {
    const lobbyOwnerChanged = oldInvState.owner.profileId !== newInvState.owner.profileId;

    if (lobbyOwnerChanged) {
      console.log('[CafeInvitationManager] Lobby owner changed:', {
        old: oldInvState.owner.profileId,
        new: newInvState.owner.profileId,
      });

      // Find the new owner in connected players
      const [ownerId, owner] =
        Object.entries(this.stores.player.currentState.players).find(
          ([_, player]) =>
            player.playerAuthId === newInvState.owner.profileId ||
            player.persistentId === newInvState.owner.profileId,
        ) || [];

      if (ownerId && owner) {
        // Grant full access to new owner
        this.stores.player.updateState({
          ...this.stores.player.currentState,
          players: {
            ...this.stores.player.currentState.players,
            [ownerId]: {
              ...owner,
              access: {
                gamepad: true,
                keyboard: true,
                mouse: true,
                touch: false,
                chat: false,
              },
            },
          },
        });

        this.events.lobbyOwnerChanged.emit({
          oldOwnerId: oldInvState.owner.profileId,
          newOwnerId: newInvState.owner.profileId,
        });
      }
    }
  }
}
